<template>
  <div :style="`height: ${height}px; overflow-y: auto; ${backgroundStyle}`">
    <div
      class="w-100 p-3 d-flex align-items-center justify-content-between"
      :class="{
        'border-bottom': showSummary,
        'bg-light': !showSummary,
        'bg-white': showSummary,
      }"
    >
      <span>
        <strong>
          {{ $t("js.backend.ballot_preview.interactive_preview", {}, { locale: backendLocale }) }}
        </strong>
        <AVIcon
          icon="info-circle"
          class="text-dark ms-1 cursor-pointer"
          data-bs-toggle="modal"
          data-bs-target="#ballotPreviewModal"
        />
      </span>

      <div class="d-flex gap-2">
        <button
          :class="`btn btn-sm ${contestsOpened ? 'btn-warning' : 'btn-primary'}`"
          @click="toggleContestsOpened"
          v-text="contestsOpened
            ? $t('js.backend.ballot_preview.close_ballot', {}, { locale: backendLocale })
            : $t('js.backend.ballot_preview.open_ballot', {}, { locale: backendLocale })"
        ></button>
        <select class="btn btn-sm btn-light dropdown-toggle" v-model="selectedLocale">
          <option
            v-for="locale in availableLocales"
            :key="`${locale}-locale`"
            :value="locale"
          >
            {{ $t("js.language_name", {}, { locale: locale }) }}
          </option>
        </select>
      </div>
    </div>

    <div
      v-if="showSummary && votingRound"
      class="w-100 px-3 py-2 d-flex align-items-center gap-1 bg-light shadow-sm"
    >
      <span v-if="votingRound.demo" class="badge text-bg-warning">
        <AVIcon icon="flask" /> {{ t("activerecord.attributes.voting_round.demo") }}
      </span>
      <span v-for="(badge, index) in getBadges(votingRound)" :key="`generated_badge_${index}`" :class="`badge text-bg-${badge.class}`">
        {{ badge.text }}
      </span>
    </div>

    <div class="p-0 m-0" :class="{ 'bg-light': showSummary }">
      <div class="container p-0 px-md-5 pt-sm-3 pt-md-5 pb-5">
        <div class="card border-0 rounded-0 shadow-lg h-100 mb-3">
          <div class="card-header no-accent d-flex flex-column flex-sm-row align-items-stretch bg-theme rounded-0 border-bottom align-items-sm-center p-0">
            <div v-if="themeImg" class="bg-light p-3 align-self-stretch d-flex align-items-center">
              <img :src="themeImg" class="me-3" style="max-height: 50px; max-width: 100%" />
            </div>
            <div class="d-flex flex-grow-1 align-items-center justify-content-between p-3">
              <h3 class="mb-0">{{ electionTitle[locale as SupportedLocale] }}</h3>
            </div>
          </div>
          <div class="card-body h-100 p-3 p-md-5" :dir="isRtl ? 'rtl' : 'ltr'">
            <template v-if="slide.data">
              <header v-if="slide.data.title" class="h1 text-center">{{ slide.data.title[locale] }}</header>
              <div v-if="slide.data.description" class="lead" v-html="slide.data.description[locale]"></div>
            </template>

            <template v-if="contestsOpened && currentItem?.itemType === 'InformationPage'">
              <div id="information-page" class="information-page">
                <h2 v-if="localizedTitle" class="mb-3">{{ localizedTitle }}</h2>
                <div v-if="localizedContent" class="information-page-content" v-html="localizedContent" />
              </div>
            </template>

            <template v-else-if="contestsOpened && currentContest">
              <AVSplitHelper
                :image-option='imageOption'
                :key="currentContest.reference"
                :contest="formattedContest"
                :contestSelection="activeContestSelection"
                :partial-results="partialResults"
                :includeLazyErrors="includeLazyErrors"
                :weight="1"
                :show-submission-helper="true"
                @update:contestSelection="updateActiveContestSelection"
                @update:complete="updateCompleted"
                @update:activePile="updateActivePile"
                @update:pending-alerts="updatePendingAlerts"
                @show-overvote-alert="handleOvervoteBlockedAlert"
              />

              <div class="d-flex mt-3 gap-3 flex-column-reverse flex-md-row">
                <button
                  v-if="itemIndex > 0"
                  class="btn btn-lg btn-ballot-outline col-12 col-md-auto"
                  @click="goBack"
                  v-text="$t('js.election_client.actions.back')"
                ></button>
                <button
                  class="btn btn-lg btn-theme ms-auto col-12 col-md-auto"
                  @click="goForward"
                  v-text="$t('js.election_client.actions.continue')"
                ></button>
              </div>
            </template>

            <template v-else>
              <template v-for="item in orderedItems" :key="item.itemReference">
                <ContestHeader
                  v-if="item.itemType === 'Contest'"
                  :contest="(formatContest(contests.find(c => c.reference === item.itemReference)) as unknown as ConferenceContest)"
                  :show-options="true"
                  :locale="locale"
                />
              </template>
              <hr>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    class="modal fade"
    id="ballotPreviewModal"
    tabindex="-1"
    :aria-labelledby="t('js.backend.ballot_preview.interactive_preview', {}, { locale: backendLocale })"
  >
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title fs-5">
            {{ t("js.backend.ballot_preview.modal.title", {}, { locale: backendLocale }) }}
          </h4>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            :aria-label="t('backend.shared.modal.close', {}, { locale: backendLocale })"
          ></button>
        </div>
        <div class="modal-body">
          <p>
            <strong>
              {{ t("js.backend.ballot_preview.modal.subtitle", {}, { locale: backendLocale }) }}
            </strong>
          </p>
          <div class="alert alert-warning my-4">
            <AVIcon icon="exclamation-triangle" class="me-1" />
            {{ t("js.backend.ballot_preview.modal.alert", {}, { locale: backendLocale }) }}
          </div>
          <p>
            <strong>
              {{ t("js.backend.ballot_preview.modal.what_to_check", {}, { locale: backendLocale }) }}
            </strong>
          </p>
          <p>{{ t("js.backend.ballot_preview.modal.examples", {}, { locale: backendLocale }) }}</p>
          <ul>
            <li>{{ t("js.backend.ballot_preview.modal.example_1", {}, { locale: backendLocale }) }}</li>
            <li>{{ t("js.backend.ballot_preview.modal.example_2", {}, { locale: backendLocale }) }}</li>
            <li>{{ t("js.backend.ballot_preview.modal.example_3", {}, { locale: backendLocale }) }}</li>
            <li>{{ t("js.backend.ballot_preview.modal.example_4", {}, { locale: backendLocale }) }}</li>
            <li>{{ t("js.backend.ballot_preview.modal.example_5", {}, { locale: backendLocale }) }}</li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <template v-if="showAlertModal">
    <div class="modal d-block" tabindex="-1" role="dialog" aria-modal="true" @click.self="closeAlertModal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ alertCategory ? t(`js.election_client.validation.alert.${alertCategory}.confirmation_title`, {}, { locale: backendLocale }) : '' }}
            </h5>
            <button type="button" class="btn-close" @click="closeAlertModal" :aria-label="t('backend.shared.modal.close', {}, { locale: backendLocale })"></button>
          </div>
          <div class="modal-body">
            <p v-for="alert in displayAlerts" :key="alert.scenario" class="mb-2">
              {{ alert.isRawMessage ? alert.feedbackMessage : t(`js.components.AVSubmissionHelper.${alert.feedbackMessage}`, alert.feedbackParams, { locale: backendLocale }) }}
            </p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-outline-primary" @click="closeAlertModal">
              {{ alertCategory ? t(`js.election_client.validation.alert.${alertCategory}.go_back`, {}, { locale: backendLocale }) : '' }}
            </button>
            <button v-if="!hasBlockingOvervote" class="btn btn-primary" @click="proceedAnyway">
              {{ alertCategory ? t(`js.election_client.validation.alert.${alertCategory}.proceed_anyway`, {}, { locale: backendLocale }) : '' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show"></div>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import ContestHeader from "@/components/shared/ContestHeader.vue";
import { rtlLanguages } from "@/entrypoints/shared/i18n_constants";
import { BallotSelection } from "@assemblyvoting/js-client";
import { useI18n } from "vue-i18n";
import {
  formatContest,
  orderContests,
  flattenOptions
} from "@/entrypoints/shared/contest_utilities";
import type {
  PropType,
  ConferenceContest,
  LocalString,
  SelectionPile,
  ContestSelection,
  ConferenceOption,
  SupportedLocale,
  ConferenceVotingRound,
  BallotSlide,
  HandRaiseLiveResults,
} from "@/types";

interface InformationPage {
  reference: string;
  title: LocalString;
  content: LocalString;
}

interface VotingRoundItem {
  itemReference: string;
  itemType: "Contest" | "InformationPage";
  position: number;
}

const { locale, t, d } = useI18n();

const props = defineProps({
  contests: {
    type: Array as PropType<ConferenceContest[]>,
    required: true,
  },
  contestPositions: {
    type: Object,
    required: true,
  },
  slide: {
    type: Object as PropType<BallotSlide>,
    required: true,
  },
  electionTitle: {
    type: Object as PropType<LocalString>,
    required: true,
  },
  themeImg: {
    type: String,
    default: null,
  },
  backgroundStyle: {
    type: String,
    default: null,
  },
  height: {
    type: Number,
    default: null,
  },
  votingRound: {
    type: Object as PropType<ConferenceVotingRound>,
    default: null,
  },
  initialLocale: {
    type: String as PropType<SupportedLocale>,
    default: "en",
  },
  backendLocale: {
    type: String as PropType<SupportedLocale>,
    default: "en",
  },
  availableLocales: {
    type: Array as PropType<SupportedLocale[]>,
    default: () => ["en"],
  },
  imageOption: {
    type: String,
    default: null
  },
  initialBallotSelection: {
    type: Object as PropType<BallotSelection | null>,
    default: null,
  },
  showSummary: {
    type: Boolean,
    default: false,
  },
  initialContestsOpened: {
    type: Boolean,
    default: false,
  },
  informationPages: {
    type: Array as PropType<InformationPage[]>,
    default: () => [],
  },
  votingRoundItems: {
    type: Array as PropType<VotingRoundItem[]>,
    default: () => [],
  },
});

const itemIndex = ref<number>(0);

const confirming = ref<boolean>(false);

const contestsOpened = ref<boolean>(props.initialContestsOpened);

const partialResults = ref<HandRaiseLiveResults>(null);

const selectedLocale = ref<SupportedLocale>(props.initialLocale);

const includeLazyErrors = ref<boolean>(false);

const activePile = ref<SelectionPile>(null);

const pendingAlerts = ref<any[]>([]);

const blockedAlert = ref<any>(null);

const showAlertModal = ref(false);

const displayAlerts = computed(() =>
  blockedAlert.value ? [blockedAlert.value] : pendingAlerts.value
);

const ballotSelection = ref<BallotSelection>(structuredClone(props.initialBallotSelection) ?? null);

const orderedContests = computed(() => orderContests(props.contests, props.contestPositions));

const orderedItems = computed<VotingRoundItem[]>(() => {
  if (props.votingRoundItems.length > 0) {
    return [...props.votingRoundItems].sort((a, b) => a.position - b.position);
  }
  return orderedContests.value.map((contest, index) => ({
    itemReference: contest.reference,
    itemType: "Contest" as const,
    position: index + 1,
  }));
});

const currentItem = computed(() => orderedItems.value[itemIndex.value]);

const isLastItem = computed(() => itemIndex.value + 1 === orderedItems.value.length);

const contestSelectionIndex = computed(() => {
  let count = 0;
  for (let i = 0; i < itemIndex.value; i++) {
    if (orderedItems.value[i].itemType === "Contest") count++;
  }
  return count;
});

const currentContest = computed(() =>
  currentItem.value?.itemType === "Contest"
    ? props.contests.find(c => c.reference === currentItem.value.itemReference)
    : null
);

const currentInformationPage = computed(() =>
  currentItem.value?.itemType === "InformationPage"
    ? props.informationPages.find(p => p.reference === currentItem.value.itemReference)
    : null
);

const localValue = (obj: LocalString | undefined) =>
  obj?.[locale.value] ?? obj?.[props.availableLocales[0]] ?? "";

const localizedTitle = computed(() => localValue(currentInformationPage.value?.title));

const localizedContent = computed(() => localValue(currentInformationPage.value?.content));

const activeContestSelection = computed(() =>
  ballotSelection.value?.contestSelections[contestSelectionIndex.value]
);

const formattedContest = computed(() => {
  if (!currentContest.value) return null;
  return formatContest(currentContest.value);
});

const isRtl = computed(() => rtlLanguages.includes(locale.value));

const alertCategory = computed(() => {
  if (displayAlerts.value.length === 0) return null;
  const scenarios = displayAlerts.value.map((a: any) => a.scenario);
  const hasOvervote = scenarios.includes('overvote');
  const hasUndervote = scenarios.some((s: string) => s === 'undervote_below_min' || s === 'undervote_between');
  const hasBlank = scenarios.includes('blank_vote');
  const count = [hasOvervote, hasUndervote, hasBlank].filter(Boolean).length;
  if (count > 1) return 'mixed';
  if (hasOvervote) return 'overvote';
  if (hasUndervote) return 'undervote';
  if (hasBlank) return 'blank';
  return 'mixed';
});

const hasBlockingOvervote = computed(() => {
  const behavior = (formattedContest.value as any)?.validationPolicy?.overvote?.behavior;
  return (
    displayAlerts.value.some((alert: any) => alert.scenario === 'overvote') &&
    (behavior === 'allow_with_error' || behavior === 'block_selection' || behavior === 'behave_as_radio_button')
  );
});

const goBack = () => {
  includeLazyErrors.value = false;
  itemIndex.value = itemIndex.value - 1;
  confirming.value = false;
};

const goForward = () => {
  if (pendingAlerts.value.length > 0) {
    showAlertModal.value = true;
    return;
  }
  navigateForward();
};

const navigateForward = () => {
  if (isLastItem.value) return;
  includeLazyErrors.value = false;
  itemIndex.value = itemIndex.value + 1;
};

const toggleContestsOpened = () => contestsOpened.value = !contestsOpened.value;

const updateCompleted = (completed: boolean) => {
  if (completed) includeLazyErrors.value = true;
};

const updateActivePile = (newPile: SelectionPile) => activePile.value = newPile;

const updatePendingAlerts = (alerts: any[]) => pendingAlerts.value = alerts;

const handleOvervoteBlockedAlert = (alert: any) => {
  pendingAlerts.value = [];
  blockedAlert.value = alert;
  showAlertModal.value = true;
};

const closeAlertModal = () => {
  showAlertModal.value = false;
  blockedAlert.value = null;
};

const proceedAnyway = () => {
  if (hasBlockingOvervote.value) return;
  showAlertModal.value = false;
  pendingAlerts.value = [];
  blockedAlert.value = null;
  includeLazyErrors.value = false;
  if (!isLastItem.value) {
    navigateForward();
  }
};

const updateActiveContestSelection = (contestSelection: ContestSelection) => {
  ballotSelection.value.contestSelections.splice(
    contestSelectionIndex.value,
    1,
    contestSelection,
  );
};

const getBadges = (vr: ConferenceVotingRound) => {
  const today = new Date();
  const fromDate = (vr.status === "scheduled" && vr.schedule_from) ? new Date(vr.schedule_from) : null;
  const toDate = (vr.status === "scheduled" && vr.schedule_to) ? new Date(vr.schedule_to) : null;
  const scheduledOpened = (!fromDate || fromDate < today) && (!toDate || toDate > today);
  const scheduledFuture = fromDate && fromDate > today;
  const scheduledPast =  toDate && toDate < today;
  const badges = [];

  if (vr.status === "open" || (vr.status === "scheduled" && scheduledOpened)) {
    badges.push({
      text: t("js.backend.ballot_preview.voting_round.open"),
      class: "primary",
    });

    if (toDate) {
      badges.push({
        text: t("js.backend.ballot_preview.voting_round.closes_at", {
          date: d(toDate, "long", locale.value)
        }),
        class: "secondary",
      });
    }
  }

  if (vr.status === "closed" || (vr.status === "scheduled" && scheduledPast) || (vr.status === "scheduled" && scheduledFuture)) {
    badges.push({
      text: t("js.backend.ballot_preview.voting_round.closed"),
      class: "warning",
    });

    if (scheduledPast) {
      badges.push({
        text: t("js.backend.ballot_preview.voting_round.closed_at", {
          date: d(toDate, "long", locale.value)
        }),
        class: "secondary",
      });
    };

    if (scheduledFuture) {
      badges.push({
        text: t("js.backend.ballot_preview.voting_round.opens_at", {
          date: d(fromDate, "long", locale.value)
        }),
        class: "secondary",
      });
    };
  }

  return badges;
}

locale.value = selectedLocale.value;

if (!ballotSelection.value) {
  const contestItems = orderedItems.value.filter(item => item.itemType === "Contest");
  ballotSelection.value = {
    reference: "dummy",
    contestSelections: contestItems.map((item) => {
      return {
        reference: item.itemReference,
        piles: [],
      } as ContestSelection;
    }),
  };
};

if (props.slide.handRaise) {
  const initial: HandRaiseLiveResults = {
    blank: {
      reference: "blank",
      results: { count: 0, percentage: 0 },
    },
  };

  flattenOptions(props.contests[0].options).forEach((option: ConferenceOption) => {
    initial[option.reference] = {
      reference: option.reference,
      results: { count: 0, percentage: 0 },
    };
  });

  partialResults.value = initial;
}

watch(selectedLocale, () => locale.value = selectedLocale.value);
</script>
